import React, { useState } from 'react';
import { Icon } from '@iconify/react';

export function TermsConditionsEditor({ terms, onChange }) {
  const [expandedIndex, setExpandedIndex] = useState(0);

  const updateTerm = (index, field, value) => {
    const next = terms.map((term, i) =>
      i === index ? { ...term, [field]: value } : term
    );
    onChange(next);
  };

  const addTerm = () => {
    const next = [...terms, { label: 'New Term:', value: '', highlight: false }];
    onChange(next);
    setExpandedIndex(next.length - 1);
  };

  const removeTerm = (index) => {
    const next = terms.filter((_, i) => i !== index);
    onChange(next);
    setExpandedIndex((prev) => {
      if (prev === index) return Math.max(0, index - 1);
      if (prev > index) return prev - 1;
      return prev;
    });
  };

  const toggleExpanded = (index) => {
    setExpandedIndex((prev) => (prev === index ? -1 : index));
  };

  return (
    <div className="coppergat-terms-editor mb-3">
      <style>{`
        .coppergat-terms-editor .term-card {
          border: 1px solid #e5e7eb;
          border-radius: 12px;
          background: #fff;
          overflow: hidden;
          margin-bottom: 10px;
          box-shadow: 0 1px 3px rgba(0,0,0,0.06);
        }
        .coppergat-terms-editor .term-card.is-highlight {
          border-color: #facc15;
          background: #fffbeb;
        }
        .coppergat-terms-editor .term-header {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 12px 14px;
          min-height: 52px;
          cursor: pointer;
          user-select: none;
          -webkit-tap-highlight-color: transparent;
        }
        .coppergat-terms-editor .term-index {
          width: 28px;
          height: 28px;
          border-radius: 50%;
          background: #111827;
          color: #fff;
          font-size: 12px;
          font-weight: 700;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }
        .coppergat-terms-editor .term-title {
          flex: 1;
          font-size: 14px;
          font-weight: 600;
          color: #111827;
          line-height: 1.3;
          white-space: pre-line;
        }
        .coppergat-terms-editor .term-body {
          padding: 0 14px 14px;
          border-top: 1px solid #f3f4f6;
        }
        .coppergat-terms-editor .term-field label {
          display: block;
          font-size: 12px;
          font-weight: 600;
          color: #6b7280;
          margin-bottom: 6px;
        }
        .coppergat-terms-editor .term-field textarea {
          width: 100%;
          border: 1px solid #d1d5db;
          border-radius: 10px;
          padding: 12px;
          font-size: 15px;
          line-height: 1.45;
          min-height: 52px;
          resize: vertical;
        }
        .coppergat-terms-editor .term-field textarea:focus {
          outline: none;
          border-color: #c8102e;
          box-shadow: 0 0 0 3px rgba(200,16,46,0.12);
        }
        .coppergat-terms-editor .term-actions {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 10px;
          margin-top: 12px;
          flex-wrap: wrap;
        }
        .coppergat-terms-editor .highlight-toggle {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          min-height: 44px;
          padding: 0 12px;
          border-radius: 999px;
          background: #f9fafb;
          border: 1px solid #e5e7eb;
          font-size: 13px;
          font-weight: 600;
          color: #374151;
        }
        .coppergat-terms-editor .highlight-toggle input {
          width: 18px;
          height: 18px;
        }
        .coppergat-terms-editor .btn-remove-term {
          min-height: 44px;
          min-width: 44px;
          border-radius: 10px;
        }
        .coppergat-terms-editor .editor-top {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 10px;
          margin-bottom: 12px;
        }
        .coppergat-terms-editor .editor-top h6 {
          margin: 0;
          font-size: 15px;
          font-weight: 700;
        }
        .coppergat-terms-editor .btn-add-term {
          min-height: 44px;
          border-radius: 10px;
          font-weight: 600;
          padding: 0 16px;
        }
        .coppergat-terms-editor .editor-hint {
          font-size: 12px;
          color: #6b7280;
          margin-bottom: 12px;
        }
      `}</style>

      <div className="editor-top">
        <h6>Terms & Conditions</h6>
        <button type="button" className="btn btn-sm btn-dark btn-add-term" onClick={addTerm}>
          <Icon icon="mdi:plus" className="me-1" />
          Add
        </button>
      </div>
      <div className="editor-hint">Tap a term to expand and edit. Optimized for mobile staff.</div>

      {terms.map((term, index) => {
        const isOpen = expandedIndex === index;
        const labelPreview = (term.label || 'Untitled term').replace(/\n/g, ' ');

        return (
          <div
            key={index}
            className={`term-card ${term.highlight ? 'is-highlight' : ''}`}
          >
            <div className="term-header" onClick={() => toggleExpanded(index)}>
              <span className="term-index">{index + 1}</span>
              <span className="term-title">{labelPreview}</span>
              <Icon
                icon={isOpen ? 'mdi:chevron-up' : 'mdi:chevron-down'}
                width={22}
                height={22}
              />
            </div>

            {isOpen && (
              <div className="term-body">
                <div className="term-field mb-3">
                  <label>Label</label>
                  <textarea
                    rows={2}
                    value={term.label || ''}
                    onChange={(e) => updateTerm(index, 'label', e.target.value)}
                    placeholder="e.g. Payment:"
                  />
                </div>
                <div className="term-field mb-2">
                  <label>Details</label>
                  <textarea
                    rows={4}
                    value={term.value || ''}
                    onChange={(e) => updateTerm(index, 'value', e.target.value)}
                    placeholder="Enter term details..."
                  />
                </div>
                <div className="term-actions">
                  <label className="highlight-toggle mb-0">
                    <input
                      type="checkbox"
                      checked={!!term.highlight}
                      onChange={(e) => updateTerm(index, 'highlight', e.target.checked)}
                    />
                    Highlight (yellow)
                  </label>
                  <button
                    type="button"
                    className="btn btn-outline-danger btn-sm btn-remove-term"
                    onClick={() => removeTerm(index)}
                    disabled={terms.length <= 1}
                    aria-label="Remove term"
                  >
                    <Icon icon="mdi:delete-outline" width={20} height={20} />
                  </button>
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
